/**
 * Documentation — static reference page explaining the tag system and the
 * analysis/probability logic to admins (and anyone new to the codebase).
 * Content is hand-mirrored from mobile/lib/constants.js, analysis.js, and
 * probability.js (kept in sync with frontend/src/lib/* per CLAUDE.md) —
 * intentionally static, not pulled live, since this is documentation of
 * stable game logic rather than operational data.
 */
import React from "react";
import PageHeader from "../components/ui/PageHeader";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const TAG_GROUPS = [
  {
    name: "Core Combo Role",
    description: "How this card fits into your combo lines",
    tags: [
      { label: "Starter", description: "Begins your main engine or combo line" },
      { label: "Extender", description: "Continues plays after the first action" },
      { label: "One-card combo", description: "Creates meaningful setup by itself" },
      { label: "Two-card combo piece", description: "Needs a specific partner card" },
      { label: "Payoff", description: "The card or line you are trying to access" },
      { label: "Enabler", description: "Unlocks plays but may not start them alone" },
    ],
  },
  {
    name: "Card Function",
    description: "What this card does mechanically",
    tags: [
      { label: "Searcher", description: "Adds cards from deck to hand" },
      { label: "Recursion", description: "Reuses spent resources from GY" },
      { label: "Engine requirement", description: "Needed for engine function" },
      { label: "Normal Summon", description: "Usually wants your Normal Summon" },
      { label: "Follow-up", description: "Helps next turn after combo resolves" },
      { label: "Recovery", description: "Helps rebuild after interruption" },
    ],
  },
  {
    name: "Interaction / Disruption",
    description: "How this card interacts with the opponent",
    tags: [
      { label: "Hand trap", description: "Interrupts from hand on opponent's turn" },
      { label: "Board breaker", description: "Helps crack established boards" },
      { label: "Removal", description: "Answers threats already on field" },
      { label: "Protection", description: "Helps your cards survive interruption" },
      { label: "Floodgate", description: "Restricts opponent's options continuously" },
      { label: "Tech card", description: "Chosen for matchup or format reasons" },
    ],
  },
  {
    name: "Turn Preference",
    description: "When this card performs best",
    tags: [
      { label: "Going first", description: "Best when setting up first" },
      { label: "Going second", description: "Best when playing into a board" },
      { label: "Flexible", description: "Good in either turn order" },
    ],
  },
  {
    name: "Risk / Consistency",
    description: "How this card affects deck consistency",
    tags: [
      { label: "Brick", description: "Awkward or weak to open in hand" },
      { label: "Garnet", description: "Bad to draw, needs to stay in deck" },
      { label: "Good opener", description: "Strong in opening hand" },
      { label: "Situational", description: "Strong only in certain game states" },
    ],
  },
  {
    name: "Deck Context",
    description: "Resolved automatically by checking this card against the rest of the current deck list — see \"Deck Context Resolution\" below",
    tags: [
      { label: "Extra Deck access", description: "Reaches into the Extra Deck for a Fusion/Synchro/Xyz/Link play" },
      { label: "Extra Deck payoff", description: "The Extra Deck card a starter is trying to reach" },
      { label: "Extra Deck bridge", description: "Connects a starter's output to its Extra Deck payoff" },
      { label: "Conditional starter", description: "Starts plays only if a specific condition or target is met" },
      { label: "Unsupported", description: "Required deck-context support is missing for this card's role" },
      { label: "Inactive", description: "Card's main role cannot currently resolve given the deck list" },
    ],
  },
];

const PROFILE_TARGETS = [
  { profile: "Combo", starters: 12, extenders: 8, handTraps: 6, other: "bricks ≤ 3" },
  { profile: "Midrange", starters: 10, extenders: 6, handTraps: 9, other: "bricks ≤ 2" },
  { profile: "Control", starters: 9, extenders: 4, handTraps: 12, other: "bricks ≤ 2" },
  { profile: "Going Second OTK", starters: 10, extenders: 6, handTraps: 6, other: "10+ board breakers, bricks ≤ 2" },
  { profile: "Rogue/Fun", starters: 9, extenders: 6, handTraps: 6, other: "bricks ≤ 4" },
];

const Section = ({ title, children }) => (
  <section className="card-celestial rounded-2xl p-6 space-y-4">
    <h2 className="text-base font-display font-bold text-celestial-text">{title}</h2>
    {children}
  </section>
);

const Documentation = () => {
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Documentation"
        subtitle="How card tags, deck ratios, and probability calculations work across mobile + web."
      />

      <Section title="Tag groups">
        <p className="text-sm text-celestial-muted">
          Every card can carry tags from these six groups. Tags drive the ratio counts, issue detection, and
          probability math below — they're the single source of truth the analysis engine reads.
        </p>
        <div className="space-y-5">
          {TAG_GROUPS.map((group) => (
            <div key={group.name}>
              <div className="text-sm font-semibold text-celestial-text">{group.name}</div>
              <div className="text-xs text-celestial-muted/70 mb-2">{group.description}</div>
              <dl className="grid sm:grid-cols-2 gap-2">
                {group.tags.map((t) => (
                  <div key={t.label} className="rounded-lg bg-surface/40 px-3 py-2">
                    <dt className="text-xs font-medium text-celestial-text">{t.label}</dt>
                    <dd className="text-xs text-celestial-muted/80">{t.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Deck profiles & ratio targets">
        <p className="text-sm text-celestial-muted">
          Each deck is assigned a profile, which sets the target ratios that issue detection and suggestions
          are measured against (<code className="text-xs">PROFILE_TARGETS</code> in lib/constants.js).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-celestial-muted/70 border-b border-celestial-border/15">
                <th className="py-2 pr-4">Profile</th>
                <th className="py-2 pr-4">Starters</th>
                <th className="py-2 pr-4">Extenders</th>
                <th className="py-2 pr-4">Hand traps</th>
                <th className="py-2">Other</th>
              </tr>
            </thead>
            <tbody className="text-celestial-text">
              {PROFILE_TARGETS.map((p) => (
                <tr key={p.profile} className="border-b border-celestial-border/10 last:border-0">
                  <td className="py-2 pr-4 font-medium">{p.profile}</td>
                  <td className="py-2 pr-4">{p.starters}</td>
                  <td className="py-2 pr-4">{p.extenders}</td>
                  <td className="py-2 pr-4">{p.handTraps}</td>
                  <td className="py-2 text-celestial-muted text-xs">{p.other}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="How ratios are calculated">
        <p className="text-sm text-celestial-muted">
          <code className="text-xs">calculateRatios(deck)</code> counts cards by tag across the Main, Extra,
          and Side decks (weighted by quantity). A few counts are unions of multiple tags rather than a single
          tag:
        </p>
        <ul className="text-sm text-celestial-muted list-disc pl-5 space-y-1">
          <li><span className="text-celestial-text">Brick-like</span> = Brick + Garnet</li>
          <li><span className="text-celestial-text">Reactive</span> = Hand trap + Board breaker</li>
          <li><span className="text-celestial-text">Combo starters</span> = Starter + One-card combo</li>
        </ul>
        <p className="text-sm text-celestial-muted">
          <code className="text-xs">detectIssues(ratios, profile)</code> then flags problems against the
          profile's targets — main deck below 40 cards or combo starters below 9 are flagged{" "}
          <span className="text-danger">critical</span>; missing the profile's starter target or running 3–4
          brick-like cards is a <span className="text-warning">warning</span>; running over 40 cards is just{" "}
          <span className="text-glow-light">info</span>. <code className="text-xs">generateSuggestions(...)</code>
          {" "}turns those gaps into concrete actions (add N starters, cut N bricks, rebalance extenders vs.
          starters, etc.).
        </p>
      </Section>

      <Section title="Hand probability engine">
        <p className="text-sm text-celestial-muted">
          Opening-hand odds are computed exactly — not via random simulation — using a hypergeometric
          distribution over a bitmask/bucket dynamic-programming approach (<code className="text-xs">
          calculateExactProbabilities</code> in lib/probability.js). Each card's tag combination becomes a
          bitmask (starter, extender, one-card combo, brick-like, hand trap, board breaker, two-card combo);
          cards sharing a mask are bucketed together so the DP only has to iterate over distinct buckets,
          not every individual card. This produces exact rates for things like "probability of opening at
          least one starter" or "probability of a brick-heavy hand," with no margin of error from sampling.
        </p>
        <p className="text-sm text-celestial-muted">
          Each drawn hand is graded <code className="text-xs">S / A / B / C / F</code> by{" "}
          <code className="text-xs">analyzeHand</code>: a perfect hand (starter + extender + hand trap, zero
          bricks) or full combo with interaction is <span className="text-celestial-text">S</span>; full combo
          alone is <span className="text-celestial-text">A</span>; a starter/extender pair with at most one
          brick is also <span className="text-celestial-text">A</span>; a confirmed playable line is{" "}
          <span className="text-celestial-text">B</span>; a bare starter with no follow-up is{" "}
          <span className="text-celestial-text">C</span>; a brick-heavy hand (2+ brick-like cards) is{" "}
          <span className="text-celestial-text">F</span>.
        </p>
      </Section>

      <Section title="Deck Context Resolution (admin override layer)">
        <p className="text-sm text-celestial-muted">
          Some cards don't have a fixed role — their tags depend on whether the rest of the deck list actually
          supports them (e.g. Instant Fusion needs a valid Fusion target in the Extra Deck; PSY-Framegear
          Gamma needs PSY-Frame Driver still in the deck). This is resolved per-deck by{" "}
          <code className="text-xs">resolveDeckContext</code>, which checks each card against a rule — in
          priority order: an admin rule from this panel, then the app's built-in static catalog, then
          AI-suggested tags, then the user's manual tags. When a rule's requirements aren't met, the card's
          role tags are dropped and replaced with "unsupported" / "inactive".
        </p>
        <p className="text-sm text-celestial-muted">
          Rules created in the Card Tag Rules page take priority over the static catalog and go live to
          mobile + web immediately via the public <code className="text-xs">/api/tag-rules/active</code>{" "}
          endpoint — no app release needed.
        </p>
        <Link to="/admin/tag-rules" className="text-sm text-glow-light hover:text-glow-mid flex items-center gap-1">
          Go to Card Tag Rules <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </Section>
    </div>
  );
};

export default Documentation;
